import { useSyncExternalStore } from 'react';
import type { NotificationKind } from './notifications';

export interface NotificationPrefs {
  toastsEnabled: boolean;
  kinds: Record<NotificationKind, boolean>;
}

const STORAGE_KEY = 'isc_notification_prefs_v1';

const DEFAULTS: NotificationPrefs = {
  toastsEnabled: true,
  kinds: {
    test_published: true,
    test_scheduled_live: true,
    job_published: true,
    general: true,
  },
};

function read(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      toastsEnabled: parsed.toastsEnabled ?? DEFAULTS.toastsEnabled,
      kinds: { ...DEFAULTS.kinds, ...(parsed.kinds || {}) },
    };
  } catch {
    return DEFAULTS;
  }
}

const listeners = new Set<() => void>();
let cache: NotificationPrefs = read();

function emit() {
  cache = read();
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) emit();
  });
}

export function getNotificationPrefs(): NotificationPrefs {
  return cache;
}

export function setNotificationPrefs(next: NotificationPrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  emit();
}

export function updateNotificationPrefs(patch: Partial<NotificationPrefs>) {
  setNotificationPrefs({ ...cache, ...patch });
}

export function setKindEnabled(kind: NotificationKind, enabled: boolean) {
  setNotificationPrefs({ ...cache, kinds: { ...cache.kinds, [kind]: enabled } });
}

export function useNotificationPrefs(): NotificationPrefs {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cache,
    () => DEFAULTS,
  );
}
